import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSelector, useDispatch } from 'react-redux'
import { WarningIcon, FileDownloadIcon } from '@rsmus/myrsm-icons'
import {
  getPaymentSelectedFilters,
  getPaymentCounts,
  setPaymentsExportStatus,
} from 'src/store/invoices/paymentSelectedFiltersSlice'
import { toPaymentSearchFilter } from 'src/utils/helpers/filterAdapter'
import ResponsiveModal from '../../../rsmCoreComponents/components/ResponsiveModal'
import { REACT_APP_EXPORT_MAX_LIMIT } from '../../../envVariables'
import { Styles } from '../../../types'
import api from '../../../api'
import tokens from '../../../styles/tokens.json'
import { useDeviceType } from '../../../rsmCoreComponents/hooks/useDeviceType'

const styles: Styles = {
  DialogTitle: () => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: '0.625rem',
  }),
  ButtonGroup: () => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    paddingTop: '1rem',
    paddingBottom: '1.5rem',
  }),
  warningContainerStyle: (theme) => ({
    width: '100%',
    background: tokens.colors.rsmRed.warning,
    display: 'flex',
    justifyContent: 'center',
    padding: '0.75rem 1rem',
    [theme.breakpoints.only('tablet')]: {
      padding: '0.75rem 5rem 1rem',
    },
  }),
  warningInnerBoxStyle: (theme) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    gap: '0.75rem',
    [theme.breakpoints.only('tablet')]: {
      padding: '0.75rem 3rem 1rem',
      maxWidth: '49rem',
    },
  }),
  warningIconStyle: {
    fontSize: '1.5rem',
    color: tokens.colors.warning.main,
    flexShrink: 0,
  },
  warningTextStyle: {
    color: tokens.colors.rsmText.primary,
    fontWeight: 400,
    lineHeight: 1.5,
    wordBreak: 'break-word',
    flex: 1,
    fontFamily: tokens.type.fontFamilies.preloMedium,
  },
  exportInvoiceHistorylabelStyle: {
    fontWeight: 300,
    textAlign: 'center',
    lineHeight: '2.5rem',
    fontSize: '2.25rem',
  },
  exportRadioGroupStyle: (theme) => ({
    padding: '0.3rem 1rem',
    textAlign: 'center',
    display: 'flex',
    flexflow: 'column',
    flexDirection: 'column',
    alignItems: 'center',
    flexWrap: 'wrap',
    [theme.breakpoints.only('desktop')]: {
      padding: '1.3rem 10.9rem',
    },
  }),
  formControlLabelStyle: (theme) => ({
    marginTop: '0.3rem',
    [theme.breakpoints.only('desktop')]: {
      padding: '0.3rem 0rem',
    },
  }),
  formControlLabelEntireStyle: {
    padding: '0.3rem 0rem',
    marginBottom: '0.3rem',
  },
}

interface ExportModalProps {
  open: boolean
  onClose: () => void
}

const PaymentHistoryExportModal = ({ open, onClose }: ExportModalProps) => {
  const getDateStamp = (): string => {
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const year = String(now.getFullYear()).slice(-2)
    return `${month}-${day}-${year}`
  }
  const DEFAULT_HEIGHT = '25.5rem'
  const ERROR_HEIGHT = '32.5rem'
  const { isDesktop } = useDeviceType()
  const dispatch = useDispatch()
  const [isExportEnabled, setIsExportEnabled] = useState<boolean>(true)
  const [isExporting, setIsExporting] = useState(false)
  const [selectedOption, setSelectedOption] = useState<
    'filtered' | 'entire' | null
  >(null)

  const paymentFilters = useSelector(getPaymentSelectedFilters)
  const { filteredPayments, totalPayments } = useSelector(getPaymentCounts)
  const isExportLimitExceeded =
    (selectedOption === 'filtered' &&
      filteredPayments > REACT_APP_EXPORT_MAX_LIMIT) ||
    (selectedOption === 'entire' && totalPayments > REACT_APP_EXPORT_MAX_LIMIT)

  const getDesktopMaxHeight = (): string =>
    !selectedOption || isExportLimitExceeded ? ERROR_HEIGHT : DEFAULT_HEIGHT

  useEffect(() => {
    setIsExportEnabled(!!selectedOption && !isExportLimitExceeded)
  }, [selectedOption, filteredPayments, totalPayments, isExportLimitExceeded])

  useEffect(() => {
    if (open) {
      if (filteredPayments > 0 && filteredPayments !== totalPayments) {
        setSelectedOption('filtered')
      } else {
        setSelectedOption('entire')
      }
    }
  }, [open, filteredPayments, totalPayments])

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const applyFilter = selectedOption === 'filtered'
      const filterPayload = toPaymentSearchFilter(paymentFilters)

      const response = await api.finance.invoiceDocument_ExportPaymentsToCsv(
        applyFilter,
        filterPayload,
      )

      if (response?.data) {
        const blob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8;',
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `Payment History Summary ${getDateStamp()}.csv`,
        )
        document.body.appendChild(link)
        link.click()
        link.remove()

        dispatch(setPaymentsExportStatus({ isSuccess: true, showAlert: false }))
        onClose()
      } else {
        onClose()
        setTimeout(() => {
          dispatch(
            setPaymentsExportStatus({ isSuccess: false, showAlert: true }),
          )
        }, 100)
      }
    } catch (error) {
      onClose()
      setTimeout(() => {
        dispatch(setPaymentsExportStatus({ isSuccess: false, showAlert: true }))
        console.error('Export failed:', error)
      }, 100)
    } finally {
      setIsExporting(false)
    }
  }
  const { t } = useTranslation()

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      aria-labelledby="export-Payment-history"
      overrideFullScreenDialog
      desktopMaxHeight={getDesktopMaxHeight()}
      desktopMaxWidth={isDesktop ? '41rem' : '37rem'}
      removeHeaderSpacing
      removeContentSpacing
      removeCloseButton
      data-testid="Lbl_Invoicing_PaymentHistoryExport_Modal">
      <Box tabIndex={-1} role="document">
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: 'absolute', top: '0.625rem', right: '0.625rem' }}>
          <CloseIcon sx={{ fontSize: '1.5rem' }} />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '2.5rem',
            flexflow: 'column',
            flexDirection: 'column',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}>
          <FileDownloadIcon
            fontSize="3.5rem"
            width="3.875rem"
            height="3.875rem"
          />
        </Box>
        <Typography
          id="export-radio-group-label"
          variant="h5"
          component="h1"
          role="heading"
          sx={styles.exportInvoiceHistorylabelStyle}>
          {t('Invoicing.PaymentHistoryExport.ExportPaymentHistory')}
        </Typography>

        <Box sx={styles.exportRadioGroupStyle}>
          <RadioGroup
            value={selectedOption}
            onChange={(e) =>
              setSelectedOption(e.target.value as 'filtered' | 'entire')
            }
            aria-labelledby="export-radio-group-label">
            {filteredPayments > 0 && filteredPayments !== totalPayments ? (
              <>
                <FormControlLabel
                  sx={styles.formControlLabelEntireStyle}
                  data-testid="Radio_PaymentHistoryExportModal_ExportCSV_Filtered"
                  value="filtered"
                  control={<Radio />}
                  label={
                    <Typography variant="body1">
                      {t(
                        'Invoicing.PaymentHistoryExport.ExportFilteredResults',
                      )}{' '}
                      ({filteredPayments} results)
                    </Typography>
                  }
                />
                <FormControlLabel
                  sx={styles.formControlLabelStyle}
                  data-testid="Radio_PaymentHistoryExportModal_ExportCSV_Entire"
                  value="entire"
                  control={<Radio />}
                  label={
                    <Typography variant="body1">
                      {t('Invoicing.PaymentHistoryExport.ExportEntireRecord')} (
                      {totalPayments} results)
                    </Typography>
                  }
                />
              </>
            ) : (
              <FormControlLabel
                sx={styles.formControlLabelStyle}
                data-testid="Radio_PaymentHistoryExportModal_ExportCSV_Entire"
                value="entire"
                control={<Radio />}
                label={
                  <Typography variant="body1">
                    {t('Invoicing.PaymentHistoryExport.ExportEntireRecord')} (
                    {totalPayments} results)
                  </Typography>
                }
              />
            )}
          </RadioGroup>
        </Box>

        {isExportLimitExceeded && (
          <Box
            sx={{
              minHeight: '6.5rem',
              transition: 'all 0.3s ease-in-out',
              opacity: 1,
              visibility: 'visible',
            }}
            role="alert"
            aria-live="assertive"
            aria-atomic="true">
            <Box sx={styles.warningContainerStyle}>
              <Box sx={styles.warningInnerBoxStyle}>
                <WarningIcon sx={styles.warningIconStyle} />
                <Typography variant="body2" sx={styles.warningTextStyle}>
                  {t('Invoicing.PaymentHistoryExport.ExportLimitWarning', {
                    exportLimit: REACT_APP_EXPORT_MAX_LIMIT,
                  })}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={styles.ButtonGroup}>
          <Button
            variant="outlined"
            onClick={onClose}
            data-testid="Btn_Invoicing_PaymentHistoryExport_Cancel">
            {t('Invoicing.PaymentHistoryExport.Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleExport}
            data-testid="Btn_Invoicing_PaymentHistoryExport_Export"
            disabled={!isExportEnabled || isExporting}
            startIcon={
              isExporting ? (
                <CircularProgress size={20} color="inherit" />
              ) : undefined
            }>
            {t('Invoicing.PaymentHistoryExport.exportToCSV')}
          </Button>
        </Box>
      </Box>
    </ResponsiveModal>
  )
}

export default PaymentHistoryExportModal
